import moment from 'moment';
import {DATE_FORMATS} from '../date-format/date-format.constants';
import {HourFormat} from './time-picker';
import {MINUTES_PER_DAY, TIME_INPUT_PATTERN, TIME_VALUE_FORMAT} from './time-picker.constants';

/**
 * Reads typed text as a time of day, returning the `HH:mm` 24h value or `null` when it does not
 * parse (see {@link TIME_INPUT_PATTERN} for what is accepted).
 *
 * - A typed am/pm suffix always wins, and then needs a 12h hour: `3:20p` is 15:20, `12am` is 00:00,
 *   while `15:00 pm` and `0:30 am` are rejected.
 * - An hour of 0 or above 12 without a suffix is plainly 24h input, in either format: `13:20`.
 * - In `12h` without a suffix, an hour from 1 to 12 is am: `3:20` is 03:20 and `12:30` is 00:30. A pm
 *   time takes a suffix, or a pick from the list.
 * - In `24h` without a suffix the hour is taken as is.
 */
export function parseTime(text: string, hourFormat: HourFormat): string | null {
  const match = TIME_INPUT_PATTERN.exec(text.trim());
  if (!match) {
    return null;
  }
  const [, colonHour, colonMinute, digits, suffix] = match;
  let hour: number;
  let minute: number;
  if (digits !== undefined) {
    // 1–2 digits are a whole hour; 3–4 are the hour followed by two minute digits.
    const hourDigits = digits.length <= 2 ? digits.length : digits.length - 2;
    hour = Number(digits.slice(0, hourDigits));
    minute = digits.length <= 2 ? 0 : Number(digits.slice(hourDigits));
  } else {
    hour = Number(colonHour);
    minute = Number(colonMinute);
  }
  if (minute > 59 || hour > 23) {
    return null;
  }

  if (suffix) {
    if (hour < 1 || hour > 12) {
      return null;
    }
    hour = to24h(hour, suffix.toLowerCase() === 'p');
  } else if (hourFormat === '12h' && hour >= 1 && hour <= 12) {
    hour = to24h(hour, false);
  }
  return `${pad(hour)}:${pad(minute)}`;
}

/** An `HH:mm` value the way a field in `hourFormat` shows it: `3:42 pm` or `15:42`. */
export function formatTime(value: string, hourFormat: HourFormat): string {
  const pattern = DATE_FORMATS[hourFormat === '12h' ? 'us' : 'default'].time;
  return moment(value, TIME_VALUE_FORMAT, true).format(pattern);
}

/** Every time of day `interval` minutes apart, from midnight: `00:00`, `00:15`, … */
export function intervalTimes(interval: number): string[] {
  const step = Number.isFinite(interval) && interval >= 1 ? Math.round(interval) : MINUTES_PER_DAY;
  const times: string[] = [];
  for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += step) {
    times.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  }
  return times;
}

/** Minutes since midnight of an `HH:mm` value. */
export function toMinutes(value: string): number {
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
}

function to24h(hour12: number, pm: boolean): number {
  return (hour12 % 12) + (pm ? 12 : 0);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
